/* ============================================================
   HERZ DER KARTEN – filter.js
   Filter-Drawer (Mobile) / Sidebar (Desktop) + AJAX-Filtering ueber
   die Section Rendering API: Filter / Sortierung / Pagination / Preis
   OHNE Page-Reload. Ohne JS navigieren die Links normal (No-JS-faehig).
   Der Drawer bleibt beim Filtern offen, weil nur Grid + Filter-Inhalt
   getauscht werden, nicht der Drawer-Rahmen. Karten-Interaktionen
   (Hover/Swatch in theme.js) sind document-delegiert -> kein Re-Init.
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {

  const drawer   = document.getElementById('filter-drawer');
  const overlay  = document.getElementById('filter-overlay');
  const openBtn  = document.getElementById('filter-drawer-open');
  const closeBtn = document.getElementById('filter-drawer-close');
  const applyBtn = document.getElementById('filter-close-apply');
  const sortSel  = document.getElementById('sort-select');

  // ---- Drawer oeffnen/schliessen (nur Mobile sichtbar; Desktop = statische Sidebar) ----
  // A11y-Doppelrolle desselben Elements: mobil ist es ein Dialog-Drawer, ab 900px eine
  // dauerhaft sichtbare Sidebar. Die Dialog-Attribute (role/aria-modal/aria-hidden)
  // muessen deshalb dem Breakpoint folgen — das statische aria-hidden="true" aus dem
  // Markup machte die komplette Desktop-Filter-UI fuer Screenreader unsichtbar
  // (Audit 2026-08-04).
  const mqDesktop = window.matchMedia('(min-width: 900px)');
  function syncFilterRole() {
    if (!drawer) return;
    if (mqDesktop.matches) {
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
      drawer.removeAttribute('aria-hidden');
    } else {
      const open = drawer.classList.contains('filter-drawer--open');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open) drawer.setAttribute('aria-modal', 'true');
      else drawer.removeAttribute('aria-modal');
    }
  }
  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('filter-drawer--open');
    overlay?.classList.add('filter-overlay--visible');
    overlay?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('filter-drawer-active');
    syncFilterRole();
    if (window.modalFocus) window.modalFocus.activate(drawer);
  }
  function closeDrawer() {
    // Guard: der Escape-Handler ruft closeDrawer() bei jedem Escape — Fokus-Restore
    // darf nur laufen, wenn der Drawer wirklich offen war.
    if (!drawer || !drawer.classList.contains('filter-drawer--open')) return;
    drawer.classList.remove('filter-drawer--open');
    overlay?.classList.remove('filter-overlay--visible');
    overlay?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('filter-drawer-active');
    syncFilterRole();
    if (window.modalFocus) window.modalFocus.deactivate();
  }
  openBtn?.addEventListener('click', openDrawer);
  closeBtn?.addEventListener('click', closeDrawer);
  overlay?.addEventListener('click', closeDrawer);
  applyBtn?.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });
  mqDesktop.addEventListener('change', syncFilterRole);
  syncFilterRole();

  // ---- AJAX-Filtering (Section Rendering API) ----
  // Section-ID dynamisch aus dem Wrapper (#shopify-section-<id>) lesen (templateabhaengig).
  const sectionBody = document.querySelector('.collection-body');
  const sectionWrap = sectionBody?.closest('.shopify-section');
  const sectionId   = sectionWrap ? sectionWrap.id.replace('shopify-section-', '') : null;

  const getGrid = () => document.querySelector('.collection-grid-wrap');

  // Laufender Request; wird beim naechsten applyUrl abgebrochen, damit eine spaeter
  // eintreffende (aeltere) Antwort das Grid der zuletzt gewaehlten Filter nicht ueberschreibt.
  let inflight = null;

  // Vertagte Kartenbilder im getauschten Grid (Audit 2026-10 PERF-14): main-collection/search
  // rendern Karten ab Position 5 mit data-defer-src; der IntersectionObserver in theme.js kennt
  // nur die Knoten vom Seitenaufbau (Muster «Below-fold-Bilder wirklich vertagen», Sections-API-
  // Falle). Nach einem Filter-Swap gibt es kein LCP-Fenster mehr zu schuetzen — die Quellen gehen
  // darum direkt auf src/srcset zurueck, das loading="lazy" der Karte regelt den Rest wie vor dem
  // Umbau. Laeuft auf dem geparsten Dokument, also bevor die Knoten ins DOM kommen. Die Regel
  // selbst ist window.hdkVertagteBilder (theme.js, geteilt mit cart-page.js; bis 20.10.2026 hier).

  async function applyUrl(url, opts = {}) {
    const push = opts.push !== false;
    // Fallback: ohne Section-Kontext klassisch navigieren (No-JS / unbekannte Seite)
    if (!sectionId) { window.location.href = url; return; }

    if (inflight) inflight.abort();
    const ctrl = new AbortController();
    inflight = ctrl;

    getGrid()?.classList.add('collection-grid-wrap--loading');
    try {
      const sep = url.indexOf('?') === -1 ? '?' : '&';
      const res = await fetch(url + sep + 'section_id=' + encodeURIComponent(sectionId), { signal: ctrl.signal });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      // 1) Produkt-Grid (inkl. Pagination / Leer-Zustand) komplett ersetzen
      const newGrid = doc.querySelector('.collection-grid-wrap');
      const curGrid = getGrid();
      if (newGrid && window.hdkVertagteBilder) window.hdkVertagteBilder(newGrid);
      if (newGrid && curGrid) curGrid.replaceWith(newGrid);
      // Badge-Wortlaut aus den Theme-Locales (theme.js, localizeReviewBadges): der
      // Beobachter des alten Grids verfaellt mit dem Swap, das neue braucht seinen eigenen.
      if (newGrid && typeof window.localizeReviewBadges === 'function') {
        newGrid.querySelectorAll('.product-grid').forEach((grid) => window.localizeReviewBadges(grid, { watch: true }));
      }

      // 2) Filter-Sektionen: nur den Body-Inhalt tauschen (Drawer-Rahmen + offener Zustand
      //    bleiben). Offene/zu Sektionen ueber das Label hinweg beibehalten -> kein Springen.
      const newBody = doc.querySelector('.filter-drawer__body');
      const curBody = document.querySelector('.filter-drawer__body');
      if (newBody && curBody) {
        const openLabels = new Set();
        curBody.querySelectorAll('details.filter-section[open]').forEach((d) => {
          const t = d.querySelector('.filter-section__title');
          if (t) openLabels.add(t.textContent.trim());
        });
        curBody.innerHTML = newBody.innerHTML;
        curBody.querySelectorAll('details.filter-section').forEach((d) => {
          const t = d.querySelector('.filter-section__title');
          if (t && openLabels.has(t.textContent.trim())) d.setAttribute('open', '');
        });
        // Kapazitaets-Slider im frischen Markup neu verdrahten (Bindings sind weg)
        initCapRanges(curBody);
        // Lag der Fokus im getauschten Inhalt, faellt er beim innerHTML-Swap auf <body>
        // und damit aus der Fokus-Falle des offenen Drawers -> zurueck auf den Container
        // (tabindex=-1 setzt modalFocus beim Oeffnen).
        if (document.body.classList.contains('filter-drawer-active') && drawer && !drawer.contains(document.activeElement)) {
          drawer.focus({ preventScroll: true });
        }
      }

      // 3) Aktiv-Zaehler im Filter-Button (erscheint nur bei aktiven Filtern)
      document.querySelector('.collection-toolbar__filter-count')?.remove();
      const newCount = doc.querySelector('.collection-toolbar__filter-count');
      const btn = document.getElementById('filter-drawer-open');
      if (btn && newCount) btn.appendChild(newCount);

      // 4) Produktzahl in der Toolbar (links neben der Sortierung) aktualisieren
      const newResults = doc.querySelector('.collection-toolbar__results');
      const curResults = document.querySelector('.collection-toolbar__results');
      if (newResults && curResults) curResults.textContent = newResults.textContent;

      // Sortier-Select an den Server-Zustand angleichen (relevant nach popstate)
      const newSort = doc.getElementById('sort-select');
      if (sortSel && newSort) sortSel.value = newSort.value;

      if (push) window.history.pushState({ hdkFilter: true }, '', url);
      if (opts.scrollToResults) {
        (document.querySelector('.collection-body') || getGrid())
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (err) {
      if (err.name === 'AbortError') return; // bewusst abgebrochen -> ein neuerer Request uebernimmt
      window.location.href = url; // bei echtem Fehler harte Navigation
    } finally {
      // Nur aufraeumen, wenn dieser Request noch der aktuelle ist (ein neuerer setzt inflight
      // bereits neu + haelt seine eigene Loading-Klasse).
      if (inflight === ctrl) {
        inflight = null;
        getGrid()?.classList.remove('collection-grid-wrap--loading');
      }
    }
  }

  // ---- Kapazitaets-Range-Slider (filter.p.m.custom.kapazitat) ----
  // Uebersetzt die Min/Max-Bereichswahl in die diskreten Listen-Filterwerte des
  // Metafelds: alle Werte im Bereich werden als filter.p.m....-Parameter gesetzt
  // (voller Bereich = Filter entfernt). Muss nach jedem Body-Swap neu initialisiert
  // werden (das Markup wird beim AJAX-Filtern ersetzt).
  function initCapRanges(root) {
    root.querySelectorAll('[data-cap-range]').forEach((box) => {
      let values;
      try { values = JSON.parse(box.querySelector('[data-cap-values]').textContent); }
      catch (err) { return; }
      if (!values.length) return;
      values.sort((a, b) => a.v - b.v);

      const param    = box.getAttribute('data-cap-param');
      const chart    = box.querySelector('[data-cap-chart]');
      const rail     = box.querySelector('[data-cap-rail]');
      const minR     = box.querySelector('[data-cap-min]');
      const maxR     = box.querySelector('[data-cap-max]');
      const minF     = box.querySelector('[data-cap-field-min]');
      const maxF     = box.querySelector('[data-cap-field-max]');
      const last     = values.length - 1;

      // Balken einmal aufbauen (Hoehe proportional zur Produktanzahl)
      const maxCount = Math.max(...values.map((x) => x.c), 1);
      chart.innerHTML = '';
      values.forEach((x) => {
        const bar = document.createElement('div');
        bar.className = 'cap-range__bar';
        bar.style.height = Math.max(3, Math.round((x.c / maxCount) * 44)) + 'px';
        chart.appendChild(bar);
      });
      const bars = chart.children;

      // Startzustand: Envelope der aktiven Werte, sonst voller Bereich
      let lo = 0, hi = last;
      const activeIdx = values.map((x, i) => (x.a ? i : -1)).filter((i) => i >= 0);
      if (activeIdx.length) { lo = activeIdx[0]; hi = activeIdx[activeIdx.length - 1]; }

      [minR, maxR].forEach((r) => { r.min = 0; r.max = last; r.step = 1; });

      function paint() {
        minR.value = lo;
        maxR.value = hi;
        minF.value = values[lo].v;
        maxF.value = values[hi].v;
        const pLo = last ? (lo / last) * 100 : 0;
        const pHi = last ? (hi / last) * 100 : 100;
        rail.style.setProperty('--cap-lo', pLo + '%');
        rail.style.setProperty('--cap-hi', pHi + '%');
        for (let i = 0; i < bars.length; i++) {
          bars[i].classList.toggle('cap-range__bar--in', i >= lo && i <= hi);
        }
      }

      function commit() {
        const params = new URLSearchParams(window.location.search);
        params.delete(param);
        if (!(lo === 0 && hi === last)) {
          for (let i = lo; i <= hi; i++) params.append(param, values[i].v);
        }
        params.delete('page');
        params.delete('section_id');
        const qs = params.toString();
        applyUrl(window.location.pathname + (qs ? '?' + qs : ''));
      }

      // Maus/Touch laeuft KOMPLETT ueber den Track (Inputs sind pointer-events:none):
      // native gestapelte Range-Inputs vergeben den Grab immer an das obere Element
      // (Max) und der Browser setzt dessen Wert bei jeder Bewegung auf die Pointer-
      // Position — bei identischen Reglern war Ziehen nach links dadurch blockiert
      // bzw. zog den Max-Thumb sichtbar mit. Eigenes Routing: naechstgelegener Regler
      // wird gegriffen; bei identischer Position entscheidet die erste Zugrichtung.
      const track = box.querySelector('.cap-range__track');
      let dragging = null; // 'lo' | 'hi' | 'pending'
      let startX = 0, startLo = 0, startHi = 0;

      function idxFromX(clientX) {
        const r = track.getBoundingClientRect();
        const inset = 9; // = CSS-Inset von Rail/Chart (halbe Thumb-Breite)
        const usable = Math.max(1, r.width - inset * 2);
        const t = Math.min(1, Math.max(0, (clientX - r.left - inset) / usable));
        return Math.round(t * last);
      }

      track.addEventListener('pointerdown', (e) => {
        if (e.button > 0) return;
        startX = e.clientX; startLo = lo; startHi = hi;
        const idx = idxFromX(e.clientX);
        if (lo === hi) {
          if (idx < lo) { dragging = 'lo'; lo = idx; }
          else if (idx > hi) { dragging = 'hi'; hi = idx; }
          else { dragging = 'pending'; } // auf dem Doppel-Thumb: Richtung abwarten
        } else {
          dragging = Math.abs(idx - lo) <= Math.abs(idx - hi) ? 'lo' : 'hi';
          if (dragging === 'lo') lo = Math.min(idx, hi); else hi = Math.max(idx, lo);
        }
        paint();
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
        e.preventDefault();
      });
      track.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        if (dragging === 'pending') {
          const dx = e.clientX - startX;
          if (Math.abs(dx) < 3) return;
          dragging = dx < 0 ? 'lo' : 'hi';
        }
        const idx = idxFromX(e.clientX);
        if (dragging === 'lo') lo = Math.min(idx, hi);
        else hi = Math.max(idx, lo);
        paint();
      });
      function endDrag(e) {
        if (!dragging) return;
        dragging = null;
        try { track.releasePointerCapture(e.pointerId); } catch (err) {}
        if (lo !== startLo || hi !== startHi) commit();
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);

      // Tastatur laeuft weiter ueber die nativen Inputs (Fokus + Pfeiltasten)
      minR.addEventListener('input', () => { lo = Math.min(parseInt(minR.value, 10), hi); paint(); });
      maxR.addEventListener('input', () => { hi = Math.max(parseInt(maxR.value, 10), lo); paint(); });
      minR.addEventListener('change', commit);
      maxR.addEventListener('change', commit);

      // Zahlenfelder: auf naechsten vorhandenen Wert snappen, dann anwenden
      function nearestIdx(n) {
        let best = 0;
        for (let i = 0; i <= last; i++) {
          if (Math.abs(values[i].v - n) < Math.abs(values[best].v - n)) best = i;
        }
        return best;
      }
      minF.addEventListener('change', () => {
        const n = parseFloat(minF.value);
        lo = isNaN(n) ? 0 : Math.min(nearestIdx(n), hi);
        paint(); commit();
      });
      maxF.addEventListener('change', () => {
        const n = parseFloat(maxF.value);
        hi = isNaN(n) ? last : Math.max(nearestIdx(n), lo);
        paint(); commit();
      });

      paint();
    });
  }
  initCapRanges(document);

  // Klick-Delegation (ueberlebt den DOM-Swap): Filter, Pagination, Reset, Leer-Zustand-Reset
  document.addEventListener('click', (e) => {
    const link = e.target.closest(
      '.collection-body .filter-checkbox, .collection-body .pagination a, .filter-drawer__reset, .collection-empty a'
    );
    if (!link?.getAttribute('href')) return;
    e.preventDefault();
    applyUrl(link.getAttribute('href'), { scrollToResults: !!link.closest('.pagination') });
  });

  // Preis anwenden (#price-apply liegt im getauschten Body -> Delegation)
  document.addEventListener('click', (e) => {
    if (!e.target.closest('#price-apply')) return;
    e.preventDefault();
    const params = new URLSearchParams(window.location.search);
    const min = document.getElementById('price-min')?.value;
    const max = document.getElementById('price-max')?.value;
    if (min) params.set('filter.v.price.gte', min); else params.delete('filter.v.price.gte');
    if (max) params.set('filter.v.price.lte', max); else params.delete('filter.v.price.lte');
    params.delete('page');
    params.delete('section_id');
    const qs = params.toString();
    applyUrl(window.location.pathname + (qs ? '?' + qs : ''));
  });

  // Sortierung (Select bleibt erhalten -> direkte Bindung). q/type/filter.* bleiben in der URL.
  sortSel?.addEventListener('change', () => {
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', sortSel.value);
    params.delete('page');
    params.delete('section_id');
    applyUrl(window.location.pathname + '?' + params.toString());
  });

  // Zurueck/Vorwaerts: aktuelle URL ohne neuen History-Eintrag nachladen
  window.addEventListener('popstate', () => {
    if (sectionId) applyUrl(window.location.href, { push: false });
  });

});
